import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { SensorPortCommunicationMode, type SubChannel } from "../../Configurations/portConfigs.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import type { EntityWithSensorAddressBase } from "../baseEntityTypes/entityWithSensorAddressBase.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    SensorElementAddressFieldsWithDefaults,
    SensorElementAddressSchemaTypes,
} from "../types/sensorElementAddress.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { ForeignLookup } from "./foreignLookup.js";

const SCHEMA_KEY = "SensorElementAddress" as const;

/**
 * The address that identifies where a SensorElement lives on its host device.
 *
 * Structurally mirrors {@link SensorClusterAddress} with an added `subChannel` slot.
 * A single cluster may produce elements on multiple sub-channels (e.g. a flowmeter
 * cluster emits PULSE_FREQUENCY and TOTAL elements); `subChannel` identifies which
 * measurement within the channel this element occupies. DIRECT mode uses the numeric
 * {@link DirectSubChannel} map; DIGITAL mode uses integer indices (0, 1, 2 …).
 *
 * It would be ideal if we could inherit this from the SensorClusterAddress, but at present the
 * zod validation doesn't support class inheritance.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SensorElementAddress>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensorElementAddress
    extends SchemaBase<SensorElementAddress, SensorElementAddressFieldsWithDefaults>
    implements EntityWithSensorAddressBase
{
    static readonly schemaKey: string = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey: string = SCHEMA_KEY;

    /** Reference to the host entity (device, zone, etc.) the cluster is attached to. */
    @validate({
        zodValidator: z.object({
            targetId: z.string(),
            targetForeignField: z.string(),
            targetCollection: z.string(),
        }),
    })
    @prop({ type: () => ForeignLookup, required: true, _id: false })
    public hostReference!: ForeignLookup;

    /** The port on the host where the cluster is connected (e.g. "S1", "I1"). */
    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public portId!: string;

    /**
     * Communication protocol used between the host and the sensor (DIRECT, SDI-12, RS-485).
     * On SensorCluster this is server-set via a pre-save hook (response-only in the DTO);
     * on SensorElement this is propagated from the parent cluster.
     */
    @validate({
        zodValidator: z.enum(Object.values(SensorPortCommunicationMode) as [string, ...string[]]),
        responseOnly: true,
    })
    @prop({ type: () => String, enum: SensorPortCommunicationMode, required: true })
    public communicationMode!: SensorPortCommunicationMode;

    /** Channel within the port. 0 for DIRECT; bus address for SDI-12/RS-485. */
    @validate({ zodValidator: z.number().int().min(0) })
    @prop({ type: () => Number, required: true })
    public channel!: number;

    /**
     * Sub-channel within the channel, stored as a non-negative integer. The meaning of
     * the value depends on the sensor's communication mode — see {@link DirectSubChannel}
     * for the DIRECT-mode map (`PULSE_FREQUENCY`, `TOTAL`, `ADC_5V`). SDI-12 and RS-485
     * do not currently have sub-channel maps, so this field is typically unset for
     * non-DIRECT sensors.
     */
    @validate({ zodValidator: z.number().int().min(0).optional() })
    @prop({ type: () => Number, required: false })
    public subChannel?: SubChannel;

    static getters = {
        sensorAddressString: (self: SensorElementAddress): string =>
            [
                self.hostReference.targetId,
                self.hostReference.targetCollection,
                self.portId,
                self.communicationMode ?? "?",
                self.channel ?? "?",
                self.subChannel ?? "?",
            ].join("."),
    } as const satisfies Record<string, (self: SensorElementAddress) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorElementAddressSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SensorElementAddress, SensorElementAddressFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SensorElementAddress.dtoValidators = getZodSchemaFromClass(
    SensorElementAddress,
) as unknown as DtoValidatorTypes<SensorElementAddressSchemaTypes>;
